/**
 * Cliente WebSocket para notificaciones en tiempo real
 * Basado en Socket.IO
 */
import io from 'socket.io-client'

class WebSocketClient {
  constructor() {
    this.socket = null
    this.isConnected = false
    this.listeners = new Map()
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 5
  }

  /**
   * Conectar al servidor WebSocket
   */
  connect(url) {
    if (this.socket) {
      console.log('🔌 WebSocket ya está inicializado (o conectándose)')
      return
    }

    console.log('🔌 Conectando a WebSocket...', url)

    try {
      this.socket = io(url, {
        transports: ['websocket', 'polling'],
        forceNew: true,
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        timeout: 5000,
      })

      this.socket.on('connect', () => {
        console.log('✅ WebSocket conectado! ID:', this.socket.id)
        this.isConnected = true
        this.reconnectAttempts = 0
      })

      this.socket.on('disconnect', (reason) => {
        console.log('❌ WebSocket desconectado. Razón:', reason)
        this.isConnected = false
      })

      this.socket.on('connect_error', (err) => {
        console.error('❌ Error de conexión WebSocket:', err)
        this.reconnectAttempts += 1

        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          console.error('🚫 Máximo de intentos de reconexión alcanzado')
        }
      })

      this.socket.on('reconnect', (attemptNumber) => {
        console.log(
          `🔄 WebSocket reconectado después de ${attemptNumber} intentos`
        )
        this.reconnectAttempts = 0
      })
    } catch (err) {
      console.error('Error al conectar WebSocket:', err)
    }
  }

  /**
   * Desconectar del servidor WebSocket
   */
  disconnect() {
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
      this.isConnected = false
      this.listeners.clear()
      console.log('🔌 WebSocket desconectado')
    }
  }

  /**
   * Suscribirse a actualizaciones de pago
   */
  subscribeToPago(pagoId) {
    if (!this.socket || !this.isConnected) {
      console.warn('⚠️  WebSocket no está conectado. Intentando conectar...')
      return false
    }

    console.log(`📡 Suscribiéndose a actualizaciones del pago ${pagoId}`)
    this.socket.emit('subscribe_payment', { pedido_id: pagoId })
    return true
  }

  /**
   * Desuscribirse de actualizaciones de pago
   */
  unsubscribeFromPago(pagoId) {
    if (!this.socket || !this.isConnected) return

    console.log(`📡 Desuscribiéndose del pago ${pagoId}`)
    this.socket.emit('unsubscribe_payment', { pedido_id: pagoId })
  }

  /**
   * Escuchar evento de actualización de pago
   */
  onPaymentUpdate(callback) {
    if (!this.socket) {
      console.warn('⚠️  WebSocket no inicializado')
      return null
    }

    const handler = (data) => {
      console.log('📨 Actualización de pago recibida:', data)
      callback(data)
    }

    this.socket.on('payment_update', handler)
    this.listeners.set('payment_update', handler)

    // Retornar función para remover el listener
    return () => {
      if (this.socket) {
        this.socket.off('payment_update', handler)
        this.listeners.delete('payment_update')
      }
    }
  }

  /**
   * Emitir evento al servidor
   */
  emit(event, data) {
    if (this.socket && this.isConnected) {
      this.socket.emit(event, data)
    } else {
      console.warn('⚠️  No se puede emitir evento, WebSocket no conectado')
    }
  }

  /**
   * Escuchar evento personalizado
   */
  on(event, callback) {
    if (!this.socket) {
      console.warn('⚠️  WebSocket no inicializado')
      return null
    }

    this.socket.on(event, callback)
    this.listeners.set(event, callback)

    return () => {
      if (this.socket) {
        this.socket.off(event, callback)
        this.listeners.delete(event)
      }
    }
  }

  /**
   * Remover listener de evento
   */
  off(event, callback) {
    if (this.socket) {
      this.socket.off(event, callback)
      this.listeners.delete(event)
    }
  }

  /**
   * Obtener estado de conexión
   */
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      socketId: this.socket?.id || null,
      reconnectAttempts: this.reconnectAttempts,
    }
  }
}

// Exportar instancia singleton
export const wsClient = new WebSocketClient()

// Exportar clase para crear instancias adicionales si es necesario
export default WebSocketClient
